import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Button, toast } from '@caratuva/ui';
import {
  createKybTosSession,
  getKybReceiverDetail,
  getKybSubmission,
  getKybTosStatus,
  getPayoutConfig,
  initiateKyb,
  recordKybTosAcceptance,
  resubmitKyb,
  savePayoutConfig,
  updateKyb,
  uploadKybFile,
  type InitiateKybInput,
  type KybReceiverDetailView,
  type KybSubmissionStatus,
  type KybSubmissionView,
  type OwnerRole,
} from '../lib/api';
import { Stepper, StepHeader, FormError, CARD_CLASS, type StepperItem } from '../components/wizard';
import {
  KybForm,
  emptyBiz,
  emptyOwner,
  kybValueToInitiateKyb,
  kybValueToUpdateKyb,
  type BizFormValue,
  type OwnerFormValue,
  type UploadKind,
} from '../components/kyb-form';
import { KycForm, emptyKyc, kycValueToInitiateKyb } from '../components/kyc-form';
import {
  KybReceiverPanel,
  KybStatusBadge,
  KybWarningsList,
  toDateInput,
} from '../components/kyb-receiver-panel';
import { TosDelivery } from '../components/tos-delivery';
import { BankAccountFields } from '../components/bank-account-fields';
import { ReferralPrompt } from '../components/referral-prompt';
import { ReferralCard } from '../components/referral-card';
import { getReferralStatus } from '../lib/api';

/*
 * GetPaid self-onboarding — a stepper the consumer walks top to bottom:
 *
 *   1. Terms    — accept the settlement partner's (BlindPay) terms of service.
 *   2. Identity — verify as a business (KYB) or an individual (KYC), uploading
 *                 the required documents.
 *   3. Pix      — register the Pix key every payout lands in (BRL).
 *   4. Review   — poll verification while it's in flight; show approved /
 *                 rejected + warnings; allow resubmit on rejection and a
 *                 post-approval edit; once verified AND a Pix key is on file,
 *                 surface a prominent "create your first invoice" CTA.
 *
 * Fiat only — money arrives by bank transfer, pays out in BRL via Pix. No
 * crypto wording anywhere. All copy comes from the onboarding.* messages.
 */

const IN_FLIGHT: ReadonlySet<KybSubmissionStatus> = new Set(['pending', 'submitted', 'verifying']);

// The Identity step uploads through the org-KYB signed-URL endpoint; the shared
// FileField takes the upload fn as a prop.
function uploadOrgKybFile(file: File, kind: UploadKind, ownerIndex?: number): Promise<string> {
  return uploadKybFile(file, kind, ownerIndex);
}

export const Route = createFileRoute('/_app/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage(): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const tosQuery = useQuery({
    queryKey: ['kyb-tos'],
    queryFn: ({ signal }) => getKybTosStatus(signal),
  });

  const kybQuery = useQuery({
    queryKey: ['kyb-submission'],
    queryFn: ({ signal }) => getKybSubmission(signal),
    // Poll while the settlement partner is still reviewing so the panel flips
    // to approved/rejected on its own. Stop on a terminal state or no submission.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && IN_FLIGHT.has(status) ? 4000 : false;
    },
    refetchIntervalInBackground: false,
  });

  const kybStatus = kybQuery.data?.status ?? null;
  const detailQuery = useQuery({
    queryKey: ['kyb-receiver-detail'],
    queryFn: ({ signal }) => getKybReceiverDetail(signal),
    enabled: kybQuery.data != null,
    refetchInterval: () => (kybStatus && IN_FLIGHT.has(kybStatus) ? 4000 : false),
    refetchIntervalInBackground: false,
  });

  const payoutQuery = useQuery({
    queryKey: ['payout-config'],
    queryFn: ({ signal }) => getPayoutConfig(signal),
  });

  const referralQuery = useQuery({
    queryKey: ['referral-status'],
    queryFn: ({ signal }) => getReferralStatus(signal),
  });

  // Capture the tos_id the settlement partner appends to the redirect URL after
  // the org accepts the terms. Strip it from the address bar immediately (so a
  // refresh can't re-fire) and record it server-side (idempotent).
  const recordTos = useMutation({
    mutationFn: (tosId: string) => recordKybTosAcceptance(tosId),
    onSuccess: () => {
      const params = new URLSearchParams(window.location.search);
      if (params.has('tos_id')) {
        params.delete('tos_id');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
      }
      void qc.invalidateQueries({ queryKey: ['kyb-tos'] });
      toast.success(
        intl.formatMessage({ id: 'onboarding.tos.recorded', defaultMessage: 'Terms accepted.' }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const tosCaptureStarted = useRef(false);
  useEffect(() => {
    if (tosCaptureStarted.current) return;
    const tosId = new URLSearchParams(window.location.search).get('tos_id');
    if (!tosId) return;
    // Guard StrictMode's double-invoked mount effect; a real refresh resets the
    // ref and retries if needed.
    tosCaptureStarted.current = true;
    recordTos.mutate(tosId);
  }, []);

  const isLoading = tosQuery.isLoading || kybQuery.isLoading || payoutQuery.isLoading;
  const isError = tosQuery.isError || kybQuery.isError;

  const tosAccepted = Boolean(tosQuery.data?.accepted);
  const kyb = kybQuery.data ?? null;
  const detail = detailQuery.data ?? null;
  const hasPayout = Boolean(payoutQuery.data?.hasBankAccount);
  const approved = kyb?.status === 'approved';

  // Which step is the org on? Drives the stepper's active/complete markers.
  const activeIndex = !tosAccepted ? 0 : !kyb ? 1 : !approved ? 3 : !hasPayout ? 2 : 3;
  const steps: StepperItem[] = useMemo(
    () => [
      {
        key: 'terms',
        label: intl.formatMessage({ id: 'onboarding.step.terms', defaultMessage: 'Terms' }),
      },
      {
        key: 'identity',
        label: intl.formatMessage({ id: 'onboarding.step.identity', defaultMessage: 'Identity' }),
      },
      {
        key: 'bank',
        label: intl.formatMessage({ id: 'onboarding.step.bankAccount', defaultMessage: 'Pix' }),
      },
      {
        key: 'status',
        label: intl.formatMessage({ id: 'onboarding.step.status', defaultMessage: 'Review' }),
      },
    ],
    [intl],
  );

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({ id: 'onboarding.title', defaultMessage: 'Get set up to receive' })}
        </h1>
        <p className="mt-1 text-sm text-navy-600">
          {intl.formatMessage({
            id: 'onboarding.description',
            defaultMessage:
              'A few quick steps and you can start collecting from abroad — the money lands in your Pix in Brazilian reais.',
          })}
        </p>
      </header>

      <ReferralPrompt />

      {isLoading ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({ id: 'onboarding.loading', defaultMessage: 'Loading…' })}
        </p>
      ) : isError ? (
        <FormError
          error={intl.formatMessage({
            id: 'onboarding.error.load',
            defaultMessage: 'Could not load your onboarding status. Refresh to try again.',
          })}
        />
      ) : (
        <>
          <Stepper steps={steps} currentIndex={activeIndex} />
          <OnboardingBody
            tosAccepted={tosAccepted}
            kyb={kyb}
            detail={detail}
            hasPayout={hasPayout}
          />
          {referralQuery.data ? (
            <div className="max-w-2xl">
              <ReferralCard
                status={referralQuery.data}
                onApplied={() => void referralQuery.refetch()}
              />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function OnboardingBody({
  tosAccepted,
  kyb,
  detail,
  hasPayout,
}: {
  tosAccepted: boolean;
  kyb: KybSubmissionView | null;
  detail: KybReceiverDetailView | null;
  hasPayout: boolean;
}): JSX.Element {
  // Terms must come first (creating the receiver requires an accepted tos_id).
  if (!tosAccepted) return <TosAcceptStep />;
  if (!kyb) return <IdentityInitiateStep />;
  if (kyb.status === 'rejected') return <RejectedStep kyb={kyb} detail={detail} />;
  if (kyb.status !== 'approved') return <PendingStep kyb={kyb} detail={detail} />;
  return <ApprovedStep detail={detail} hasPayout={hasPayout} />;
}

// ── Step 1: settlement-partner ToS ────────────────────────────────────────────

function TosAcceptStep(): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const start = useMutation({
    mutationFn: () => createKybTosSession(`${window.location.origin}/onboarding`),
    onSuccess: async (session) => {
      // The dev mock returns a non-navigable URL on `mock.blindpay.local`.
      // Short-circuit by recording a synthetic tos_id so local dev needs no
      // real round-trip.
      if (session.url.includes('mock.blindpay.local')) {
        try {
          await recordKybTosAcceptance(`tos_mock_${session.idempotencyKey}`);
          await qc.invalidateQueries({ queryKey: ['kyb-tos'] });
          toast.success(
            intl.formatMessage({
              id: 'onboarding.tos.recorded',
              defaultMessage: 'Terms accepted.',
            }),
          );
        } catch (err) {
          toast.error(err instanceof Error ? err.message : String(err));
        }
        return;
      }
      // Real partner: hand off to the hosted ToS page. It redirects back to
      // /onboarding with `?tos_id=...`, captured by OnboardingPage.
      window.location.href = session.url;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return <TosDelivery submitting={start.isPending} onAccept={() => start.mutate()} />;
}

// ── Step 2: identity (business KYB vs individual KYC) ──────────────────────────

function isOwnerRole(r: string | null | undefined): r is OwnerRole {
  return r === 'beneficial_owner' || r === 'beneficial_controlling' || r === 'controlling_person';
}

// Seed the business form from the live receiver detail. Document fields stay
// empty — stored document URLs are never exposed, so a resubmit re-uploads them.
function bizFromDetail(detail: KybReceiverDetailView | null): BizFormValue {
  const base = emptyBiz();
  if (!detail) return base;
  return {
    ...base,
    legalName: detail.legalName ?? '',
    cnpj: detail.taxId ?? '',
    email: detail.email ?? '',
    formationDate: toDateInput(detail.formationDate),
    website: detail.website ?? '',
    addressLine1: detail.address?.line1 ?? '',
    addressLine2: detail.address?.line2 ?? '',
    city: detail.address?.city ?? '',
    stateProvinceRegion: detail.address?.stateProvinceRegion ?? '',
    postalCode: detail.address?.postalCode ?? '',
    country: detail.address?.country ?? base.country,
  };
}

// Seed owner rows from the receiver detail (identity fields only; address /
// ID-doc / document fields are re-entered).
function ownersFromDetail(detail: KybReceiverDetailView | null): OwnerFormValue[] {
  if (!detail || detail.owners.length === 0) return [emptyOwner()];
  return detail.owners.map((o) => {
    const base = emptyOwner();
    return {
      ...base,
      role: isOwnerRole(o.role) ? o.role : base.role,
      firstName: o.firstName ?? '',
      lastName: o.lastName ?? '',
      dateOfBirth: toDateInput(o.dateOfBirth),
      taxId: o.taxId ?? '',
      country: o.country ?? base.country,
    };
  });
}

function IdentityInitiateStep(): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();
  const [entityType, setEntityType] = useState<'individual' | 'business'>('business');

  const initiate = useMutation({
    mutationFn: (payload: InitiateKybInput) => initiateKyb(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kyb-submission'] });
      void qc.invalidateQueries({ queryKey: ['kyb-receiver-detail'] });
      toast.success(
        intl.formatMessage({
          id: 'onboarding.kyb.submitted',
          defaultMessage: 'Submitted for review. We will email you as soon as it is approved.',
        }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const submitLabel = intl.formatMessage({
    id: 'onboarding.kyb.submit',
    defaultMessage: 'Submit for review',
  });
  const submittingLabel = intl.formatMessage({
    id: 'onboarding.kyb.submitting',
    defaultMessage: 'Submitting…',
  });

  return (
    <div className="max-w-2xl space-y-6">
      <div className={CARD_CLASS}>
        <p className="text-sm font-semibold text-navy-900">
          {intl.formatMessage({
            id: 'onboarding.entityType',
            defaultMessage: 'How are you receiving?',
          })}
        </p>
        <p className="mt-1 text-sm text-navy-600">
          {intl.formatMessage({
            id: 'onboarding.entityType.hint',
            defaultMessage:
              'Choose whether you receive as a business (company) or as an individual (yourself).',
          })}
        </p>
        <div className="mt-3 flex gap-2">
          <Button
            type="button"
            variant={entityType === 'business' ? 'default' : 'outline'}
            onClick={() => setEntityType('business')}
          >
            {intl.formatMessage({
              id: 'onboarding.entityType.business',
              defaultMessage: 'A business',
            })}
          </Button>
          <Button
            type="button"
            variant={entityType === 'individual' ? 'default' : 'outline'}
            onClick={() => setEntityType('individual')}
          >
            {intl.formatMessage({
              id: 'onboarding.entityType.individual',
              defaultMessage: 'An individual',
            })}
          </Button>
        </div>
      </div>

      {entityType === 'business' ? (
        <KybForm
          initialBiz={emptyBiz()}
          initialOwners={[emptyOwner()]}
          submitting={initiate.isPending}
          submitLabel={submitLabel}
          submittingLabel={submittingLabel}
          heading={
            <StepHeader
              title={intl.formatMessage({
                id: 'onboarding.kyb.title',
                defaultMessage: 'Step 2 — Verify your business',
              })}
              description={intl.formatMessage({
                id: 'onboarding.kyb.subtitle',
                defaultMessage:
                  'We need to confirm your business so it can receive settlements in reais via Pix. All fields and documents are required.',
              })}
            />
          }
          upload={uploadOrgKybFile}
          onSubmit={(value) => initiate.mutate(kybValueToInitiateKyb(value))}
        />
      ) : (
        <KycForm
          initialValue={{ ...emptyKyc() }}
          submitting={initiate.isPending}
          submitLabel={submitLabel}
          submittingLabel={submittingLabel}
          heading={
            <StepHeader
              title={intl.formatMessage({
                id: 'onboarding.kyc.title',
                defaultMessage: 'Step 2 — Verify your identity',
              })}
              description={intl.formatMessage({
                id: 'onboarding.kyc.subtitle',
                defaultMessage:
                  'We need to confirm who you are so you can receive settlements in reais via Pix. All fields and documents are required.',
              })}
            />
          }
          upload={uploadOrgKybFile}
          onSubmit={(value) => initiate.mutate(kycValueToInitiateKyb(value))}
        />
      )}
    </div>
  );
}

// ── Step 4 (rejected): correct-and-resubmit ───────────────────────────────────

// GetPaid's receiver detail is business-shaped, so we can't reliably tell an
// individual submission apart after the fact; resubmit re-presents the business
// KYB form seeded from the detail. (Individual resubmit re-enters via the same
// owner-carried identity fields.)
function ResubmitForm({ detail }: { detail: KybReceiverDetailView | null }): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const resubmit = useMutation({
    mutationFn: (payload: InitiateKybInput) => resubmitKyb(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kyb-submission'] });
      void qc.invalidateQueries({ queryKey: ['kyb-receiver-detail'] });
      toast.success(
        intl.formatMessage({
          id: 'onboarding.kyb.resubmit.done',
          defaultMessage: 'Submitted — our partner is reviewing again.',
        }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <KybForm
      initialBiz={bizFromDetail(detail)}
      initialOwners={ownersFromDetail(detail)}
      submitting={resubmit.isPending}
      submitLabel={intl.formatMessage({
        id: 'onboarding.kyb.resubmit.cta',
        defaultMessage: 'Submit again',
      })}
      submittingLabel={intl.formatMessage({
        id: 'onboarding.kyb.resubmit.submitting',
        defaultMessage: 'Submitting…',
      })}
      heading={
        <StepHeader
          title={intl.formatMessage({
            id: 'onboarding.kyb.resubmit.title',
            defaultMessage: 'Fix and submit again',
          })}
          description={intl.formatMessage({
            id: 'onboarding.kyb.resubmit.subtitle',
            defaultMessage:
              'We kept your previous details. Fix the points above, re-upload your documents, then submit again.',
          })}
        />
      }
      upload={uploadOrgKybFile}
      onSubmit={(value) => resubmit.mutate(kybValueToInitiateKyb(value))}
    />
  );
}

function RejectedStep({
  kyb,
  detail,
}: {
  kyb: KybSubmissionView;
  detail: KybReceiverDetailView | null;
}): JSX.Element {
  const intl = useIntl();
  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-red-900">
            {intl.formatMessage({
              id: 'onboarding.status.rejectedTitle',
              defaultMessage: 'We could not verify you',
            })}
          </h2>
          <KybStatusBadge status={kyb.status} />
        </div>
        <p className="text-sm text-red-800">
          {intl.formatMessage({
            id: 'onboarding.status.rejectedBody',
            defaultMessage:
              'Our partner could not verify this submission. Check the points below, fix your details, and submit again.',
          })}
        </p>
        <KybWarningsList
          kycWarnings={detail?.kycWarnings ?? []}
          fraudWarnings={detail?.fraudWarnings ?? []}
        />
      </div>
      {/* Remount once the receiver detail loads so the form seeds from it
          (KybForm captures initial values at mount). Stable key after that so
          typing isn't wiped by background refetches. */}
      <ResubmitForm key={detail?.available ? detail.receiverId : 'pending'} detail={detail} />
    </div>
  );
}

// ── Step 4 (pending): review in progress ──────────────────────────────────────

function PendingStep({
  kyb,
  detail,
}: {
  kyb: KybSubmissionView;
  detail: KybReceiverDetailView | null;
}): JSX.Element {
  const intl = useIntl();
  return (
    <div className="max-w-2xl space-y-6">
      <div className={`space-y-3 ${CARD_CLASS}`}>
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-navy-900">
            {intl.formatMessage({
              id: 'onboarding.status.pendingTitle',
              defaultMessage: 'Review in progress',
            })}
          </h2>
          <KybStatusBadge status={kyb.status} />
        </div>
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'onboarding.status.pendingBody',
            defaultMessage:
              'Our partner is reviewing your details. This usually takes a little while — we will email you the moment it is done. You can leave this page.',
          })}
        </p>
      </div>
      <KybReceiverPanel detail={detail} />
    </div>
  );
}

// ── Step 4 (approved): Pix step + edit + completion CTA ────────────────────────

// Post-approval edit, posting to the update endpoint (keeps the receiver
// approved until re-review flips it to verifying). Documents/owners optional —
// the receiver already holds them.
function EditForm({
  detail,
  onDone,
}: {
  detail: KybReceiverDetailView | null;
  onDone: () => void;
}): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const edit = useMutation({
    mutationFn: (payload: InitiateKybInput) => updateKyb(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kyb-submission'] });
      void qc.invalidateQueries({ queryKey: ['kyb-receiver-detail'] });
      toast.success(
        intl.formatMessage({
          id: 'onboarding.edit.done',
          defaultMessage: 'Changes submitted — our partner is reviewing them.',
        }),
      );
      onDone();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <KybForm
      initialBiz={bizFromDetail(detail)}
      initialOwners={ownersFromDetail(detail)}
      docsOptional
      ownersOptional
      taxIdReadOnly
      submitting={edit.isPending}
      submitLabel={intl.formatMessage({
        id: 'onboarding.edit.submit',
        defaultMessage: 'Submit changes',
      })}
      submittingLabel={intl.formatMessage({
        id: 'onboarding.edit.submitting',
        defaultMessage: 'Submitting…',
      })}
      heading={
        <div>
          <StepHeader
            title={intl.formatMessage({
              id: 'onboarding.edit.title',
              defaultMessage: 'Edit your information',
            })}
            description={intl.formatMessage({
              id: 'onboarding.edit.subtitle',
              defaultMessage:
                "Update your details with our partner. Documents are optional — upload only what you want to replace. Changes are re-reviewed and may show as 'in review' until re-approved.",
            })}
          />
          <p className="mt-2 text-xs text-navy-500">
            {intl.formatMessage({
              id: 'onboarding.edit.note',
              defaultMessage: 'You can keep receiving while the review is in progress.',
            })}
          </p>
        </div>
      }
      upload={uploadOrgKybFile}
      onSubmit={(value) => edit.mutate(kybValueToUpdateKyb(value))}
    />
  );
}

function PixStep({ hasPayout }: { hasPayout: boolean }): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: (pixKey: string) => savePayoutConfig({ pixKey }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payout-config'] });
      toast.success(
        intl.formatMessage({ id: 'onboarding.bank.saved', defaultMessage: 'Pix key saved.' }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className={`space-y-4 ${CARD_CLASS}`}>
      <StepHeader
        title={intl.formatMessage({
          id: 'onboarding.bank.title',
          defaultMessage: 'Step 3 — Where should we send your money?',
        })}
        description={intl.formatMessage({
          id: 'onboarding.bank.subtitle',
          defaultMessage:
            'Enter the Pix key where you want to receive. This is where every payout lands in reais.',
        })}
      />
      {hasPayout ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-sm font-semibold text-emerald-900">
            {intl.formatMessage({
              id: 'onboarding.bank.savedTitle',
              defaultMessage: 'Pix key on file',
            })}
          </p>
          <p className="mt-1 text-sm text-emerald-800">
            {intl.formatMessage({
              id: 'onboarding.bank.savedBody',
              defaultMessage: 'Your payouts will be sent to the Pix key you registered.',
            })}
          </p>
        </div>
      ) : null}
      <BankAccountFields submitting={save.isPending} onSubmit={(pixKey) => save.mutate(pixKey)} />
    </div>
  );
}

function ApprovedStep({
  detail,
  hasPayout,
}: {
  detail: KybReceiverDetailView | null;
  hasPayout: boolean;
}): JSX.Element {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  const done = hasPayout;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-3 rounded-md border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-emerald-900">
            {intl.formatMessage({
              id: 'onboarding.approved.title',
              defaultMessage: 'You are verified',
            })}
          </h2>
          <KybStatusBadge status="approved" />
          <div className="ml-auto">
            <Button type="button" variant="outline" onClick={() => setEditing((v) => !v)}>
              {editing
                ? intl.formatMessage({
                    id: 'onboarding.edit.cancel',
                    defaultMessage: 'Cancel editing',
                  })
                : intl.formatMessage({
                    id: 'onboarding.edit.cta',
                    defaultMessage: 'Edit information',
                  })}
            </Button>
          </div>
        </div>
        <p className="text-sm text-emerald-800">
          {intl.formatMessage({
            id: 'onboarding.approved.body',
            defaultMessage:
              'Your identity is verified. Add or confirm your Pix key below, then you are ready to get paid.',
          })}
        </p>
      </div>

      {editing ? (
        // Remount once the receiver detail loads so the form seeds from it.
        <EditForm
          key={detail?.available ? detail.receiverId : 'pending'}
          detail={detail}
          onDone={() => setEditing(false)}
        />
      ) : (
        <>
          <KybReceiverPanel detail={detail} />
          <PixStep hasPayout={hasPayout} />
          {done ? <DoneCta /> : null}
        </>
      )}
    </div>
  );
}

// Full completion — verified AND a Pix key is on file. Prominent CTA to create
// the first invoice.
function DoneCta(): JSX.Element {
  const intl = useIntl();
  return (
    <div className="space-y-3 rounded-md border border-emerald-300 bg-emerald-50 p-5 text-center">
      <h2 className="text-lg font-semibold text-emerald-900">
        {intl.formatMessage({ id: 'onboarding.done.title', defaultMessage: 'You are all set!' })}
      </h2>
      <p className="text-sm text-emerald-800">
        {intl.formatMessage({
          id: 'onboarding.done.body',
          defaultMessage:
            'Your identity is verified and your Pix key is on file. Create your first invoice and start getting paid.',
        })}
      </p>
      <div className="flex justify-center">
        <Button asChild>
          <Link to="/invoices/new">
            {intl.formatMessage({
              id: 'onboarding.done.cta',
              defaultMessage: 'Create your first invoice',
            })}
          </Link>
        </Button>
      </div>
    </div>
  );
}
